import type React from 'react';
import cn from '../utils/cn';

type KoobSelectableCardProps = {
  selected?: boolean;
  disabled?: boolean;
  title: React.ReactNode;
  description?: React.ReactNode;
  onClick?: () => void;
  /** Tighter card without the indicator, for options laid out side by side */
  compact?: boolean;
  'data-testid'?: string;
};

// A click-to-toggle card with a radio-style circle (unselected) / circle-check
// (selected) indicator. Used for opt-in choices where the option needs room
// for a description, unlike a bare toggle or checkbox.
export default function KoobSelectableCard({
  selected = false,
  disabled = false,
  title,
  description,
  onClick,
  compact = false,
  'data-testid': dataCy,
}: KoobSelectableCardProps) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-pressed={selected}
      data-testid={dataCy}
      className={cn(
        'flex w-full min-w-0 items-start text-left transition-colors',
        compact
          ? 'rounded border px-2.5 py-2'
          : 'gap-3 rounded-xl border-2 p-4',
        selected ? 'border-k-orange' : 'border-gray-200 bg-white',
        selected && (compact ? 'bg-k-orange/10' : 'bg-k-orange/5'),
        disabled ? 'cursor-default' : 'hover:border-k-orange/60',
      )}
      // no normalize.css: force a solid border (UA default for buttons is outset)
      style={{ borderStyle: 'solid' }}
    >
      {!compact && (
        <i
          className={cn(
            'mt-0.5 text-xl leading-none',
            selected
              ? 'fa-solid fa-circle-check text-k-orange'
              : 'fa-regular fa-circle text-gray-300',
          )}
        />
      )}
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-semibold text-k-h">{title}</span>
        {description && (
          <span className={cn(compact ? 'text-xs' : 'text-sm', 'text-k-p')}>
            {description}
          </span>
        )}
      </span>
    </button>
  );
}
